import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import Toast from '../../../shared/components/ui/Toast';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { useHighlightsTacticsData } from '../hooks/useHighlightsTacticsData';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { tacticsImagesService, TacticsImage } from '../../../shared/services/tacticsImagesService';
import FolderTree from '../components/FolderTree';
import FileExplorer from '../components/FileExplorer';
import AssetDetailsDrawer from '../components/AssetDetailsDrawer';
import EditHighlightModal from '../components/EditHighlightModal';
import DeleteHighlightModal from '../components/DeleteHighlightModal';
import DeleteTacticsModal from '../components/DeleteTacticsModal';
import { CaptureItem, ToastState } from '../types';
import { BreadcrumbItem, buildBreadcrumbs } from '../utils';

const HighlightsTacticsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganization } = useOrg();
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: ''
  });
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';

  // State
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>('');
  const [expandedFolders, setExpandedFolders] = useState(() => new Set<string>(['root']));
  const [selectedAsset, setSelectedAsset] = useState<CaptureItem | null>(null);
  const [isAssetDrawerOpen, setIsAssetDrawerOpen] = useState(false);
  const [highlightData, setHighlightData] = useState<VideoHighlight | null>(null);
  const [tacticsData, setTacticsData] = useState<TacticsImage | null>(null);
  const [isEditHighlightModalOpen, setIsEditHighlightModalOpen] = useState(false);
  const [isDeleteHighlightModalOpen, setIsDeleteHighlightModalOpen] = useState(false);
  const [isDeleteTacticsModalOpen, setIsDeleteTacticsModalOpen] = useState(false);
  const [editHighlightId, setEditHighlightId] = useState<string | null>(null);
  const [deleteHighlightData, setDeleteHighlightData] = useState<{ highlightId: string; highlightName: string } | null>(null);
  const [deleteTacticsData, setDeleteTacticsData] = useState<{ tacticsId: string; tacticsName: string } | null>(null);

  // Custom hooks
  const { folders, files, folderTree, isLoading, tournamentsData, loadFolderContents, loadFolderTree } =
    useHighlightsTacticsData(orgId);

  // Load folder tree on mount
  useEffect(() => {
    if (orgId) {
      loadFolderTree();
    }
  }, [orgId, loadFolderTree]);

  // Build breadcrumbs with names instead of IDs
  const breadcrumbsWithNames = useMemo(() => {
    const crumbs = buildBreadcrumbs(selectedFolderPath);
    
    // Create lookup maps for tournament and match names
    const tournamentMap = new Map<string, string>();
    const matchMap = new Map<string, string>();
    
    tournamentsData.forEach((tournament) => {
      tournamentMap.set(tournament.id, tournament.name);
      tournament.matches.forEach((match) => {
        // Use match.name from the matches table
        matchMap.set(match.id, match.name);
      });
    });

    // Update breadcrumb names - resolve each level based on the path
    const updatedCrumbs = crumbs.map((crumb) => {
      if (crumb.id === 'root') {
        return crumb;
      }

      const parts = crumb.path?.split('/').filter(Boolean) || [];
      
      if (parts.length === 1) {
        // Tournament level - first part is tournament ID
        const tournamentName = tournamentMap.get(parts[0]);
        return { ...crumb, name: tournamentName || parts[0] };
      } else if (parts.length === 2) {
        // Match level - second part is match ID
        const matchId = parts[1];
        const matchName = matchMap.get(matchId);
        if (matchName) {
          return { ...crumb, name: matchName };
        }
        // Fallback: try to find match in any tournament
        for (const tournament of tournamentsData) {
          const match = tournament.matches.find(m => m.id === matchId);
          if (match && match.name) {
            return { ...crumb, name: match.name };
          }
        }
        return { ...crumb, name: matchId };
      } else if (parts.length === 3) {
        // Highlights or Tactics folder - third part is asset type
        const assetType = parts[2];
        const displayName = assetType === 'highlights' ? 'Highlights' : assetType === 'tactics' ? 'Tactics' : assetType;
        return { ...crumb, name: displayName };
      }

      return crumb;
    });

    // Second pass: ensure match breadcrumbs have names when we're at asset level
    // This handles the case where the match breadcrumb wasn't updated in the first pass
    return updatedCrumbs.map((crumb, index) => {
      const parts = crumb.path?.split('/').filter(Boolean) || [];
      
      // If this breadcrumb still shows an ID and it's a match level (2 parts)
      if (parts.length === 2 && crumb.name === parts[1]) {
        // Try to find the match name
        const matchId = parts[1];
        const matchName = matchMap.get(matchId);
        if (matchName) {
          return { ...crumb, name: matchName };
        }
        // Fallback search
        for (const tournament of tournamentsData) {
          const match = tournament.matches.find(m => m.id === matchId);
          if (match && match.name) {
            return { ...crumb, name: match.name };
          }
        }
      }
      
      return crumb;
    });
  }, [selectedFolderPath, tournamentsData]);

  // Handlers
  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleFolderClick = (folder: CaptureItem) => {
    const folderPath = folder.path || '';
    setSelectedFolderPath(folderPath);
    loadFolderContents(folderPath);
  };

  const handleBreadcrumbClick = (crumb: BreadcrumbItem) => {
    if (crumb.id === 'root') {
      setSelectedFolderPath('');
      loadFolderContents('');
    } else if (crumb.path) {
      setSelectedFolderPath(crumb.path);
      loadFolderContents(crumb.path);
    }
  };

  const handleRefresh = () => {
    loadFolderContents(selectedFolderPath);
    loadFolderTree();
  };

  const handleViewAsset = async (asset: CaptureItem) => {
    // Open drawer for eye icon click
    setSelectedAsset(asset);
    setIsAssetDrawerOpen(true);
    setHighlightData(null);
    setTacticsData(null);
    
    // Fetch highlight or tactics data based on asset ID
    if (asset.id.startsWith('highlight-')) {
      const highlightId = asset.id.replace('highlight-', '');
      const result = await videoHighlightsService.getHighlightById(highlightId);
      if (result.success && result.data) {
        setHighlightData(result.data);
      }
    } else if (asset.id.startsWith('tactics-')) {
      const tacticsId = asset.id.replace('tactics-', '');
      const result = await tacticsImagesService.getTacticsImageById(tacticsId);
      if (result.success && result.data) {
        setTacticsData(result.data);
      }
    }
  };

  const handleRowClickAsset = (asset: CaptureItem) => {
    // Navigate to detail page on row click for highlights
    if (asset.id.startsWith('highlight-')) {
      const highlightId = asset.id.replace('highlight-', '');
      navigate(`/o/${orgId}/capture/highlight/${highlightId}`);
    }
    // Tactics don't have detail pages, so no action on row click
  };

  const handleOpenStorageLink = (asset: CaptureItem) => {
    // Open storage link in new tab
    if (asset.storagePath) {
      const cdnUrl = `https://cdn.capture.kridah.com/${asset.storagePath}`;
      window.open(cdnUrl, '_blank');
    }
  };

  const handleDeleteHighlight = async (highlightId: string, highlightName: string) => {
    // Fetch highlight data if not already loaded
    if (!highlightData || highlightData.id !== highlightId) {
      const result = await videoHighlightsService.getHighlightById(highlightId);
      if (result.success && result.data) {
        setHighlightData(result.data);
      } else {
        // If fetch fails, still allow deletion but with limited info
        console.error('Failed to fetch highlight data:', result.error);
      }
    }
    setDeleteHighlightData({ highlightId, highlightName });
    setIsDeleteHighlightModalOpen(true);
  };

  const handleEditHighlight = (highlightId: string) => {
    setEditHighlightId(highlightId);
    setIsEditHighlightModalOpen(true);
  };

  const handleDeleteTactics = (tacticsId: string, tacticsName: string) => {
    setDeleteTacticsData({ tacticsId, tacticsName });
    setIsDeleteTacticsModalOpen(true);
  };

  const handleDeleteHighlightSuccess = () => {
    loadFolderContents(selectedFolderPath);
    loadFolderTree();
    setIsDeleteHighlightModalOpen(false);
    setDeleteHighlightData(null);
    // Close drawer if it's open for the deleted highlight
    if (selectedAsset?.id.startsWith('highlight-') && deleteHighlightData) {
      const highlightId = selectedAsset.id.replace('highlight-', '');
      if (highlightId === deleteHighlightData.highlightId) {
        setIsAssetDrawerOpen(false);
        setSelectedAsset(null);
        setHighlightData(null);
      }
    }
  };

  const handleDeleteTacticsSuccess = () => {
    loadFolderContents(selectedFolderPath);
    loadFolderTree();
    setIsDeleteTacticsModalOpen(false);
    setDeleteTacticsData(null);
    // Close drawer if it's open for the deleted tactics image
    if (selectedAsset?.id.startsWith('tactics-') && deleteTacticsData) {
      const tacticsId = selectedAsset.id.replace('tactics-', '');
      if (tacticsId === deleteTacticsData.tacticsId) {
        setIsAssetDrawerOpen(false);
        setSelectedAsset(null);
        setTacticsData(null);
      }
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      isVisible: true,
      type,
      message
    });
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="w-full lg:w-64 bg-white border border-gray-200 rounded-xl">
          <div className="p-4 space-y-1">
            <FolderTree
              node={folderTree}
              expandedFolders={expandedFolders}
              selectedFolderPath={selectedFolderPath}
              onToggleFolder={toggleFolder}
              onFolderClick={handleFolderClick}
            />
          </div>
        </div>

        <div className="flex-1">
          <FileExplorer
            folders={folders}
            files={files}
            videosByFolder={{}}
            uploadingFiles={new Map()}
            selectedFolderPath={selectedFolderPath}
            globalSearch=""
            isLoading={isLoading}
            stuckVideoIds={new Set()}
            stuckVideos={[]}
            onFolderClick={handleFolderClick}
            onBreadcrumbClick={handleBreadcrumbClick}
            onRefresh={handleRefresh}
            onViewVideo={() => {}}
            onViewAsset={handleViewAsset}
            onRowClickAsset={handleRowClickAsset}
            onOpenStorageLink={handleOpenStorageLink}
            onEditHighlight={handleEditHighlight}
            onDeleteHighlight={handleDeleteHighlight}
            onDeleteTactics={handleDeleteTactics}
            customBreadcrumbs={breadcrumbsWithNames}
          />
        </div>
      </div>

      {/* Asset Details Drawer */}
      <AssetDetailsDrawer
        isOpen={isAssetDrawerOpen}
        onClose={() => {
          setIsAssetDrawerOpen(false);
          setSelectedAsset(null);
          setHighlightData(null);
          setTacticsData(null);
        }}
        asset={selectedAsset}
        highlightData={highlightData}
        tacticsData={tacticsData}
        onEditHighlight={handleEditHighlight}
        onDeleteHighlight={handleDeleteHighlight}
        onDeleteTactics={handleDeleteTactics}
      />

      {/* Edit Highlight Modal */}
      {editHighlightId && (
        <EditHighlightModal
          isOpen={isEditHighlightModalOpen}
          highlight={
            highlightData && highlightData.id === editHighlightId
              ? highlightData
              : null
          }
          highlightId={editHighlightId}
          onClose={() => {
            setIsEditHighlightModalOpen(false);
            setEditHighlightId(null);
          }}
          onSuccess={async () => {
            // Reload folder tree first (which reloads tournaments data)
            await loadFolderTree();
            // Then reload folder contents with updated data (force reload tournaments data)
            await loadFolderContents(selectedFolderPath, true);
            // Refresh highlight data if drawer is open
            if (selectedAsset?.id.startsWith('highlight-') && editHighlightId) {
              const highlightId = selectedAsset.id.replace('highlight-', '');
              if (highlightId === editHighlightId) {
                const result = await videoHighlightsService.getHighlightById(highlightId);
                if (result.success && result.data) {
                  setHighlightData(result.data);
                } else {
                  // If highlight is now inactive, close the drawer
                  setIsAssetDrawerOpen(false);
                  setSelectedAsset(null);
                  setHighlightData(null);
                }
              }
            }
            setIsEditHighlightModalOpen(false);
            setEditHighlightId(null);
          }}
        />
      )}

      {/* Delete Highlight Modal */}
      {deleteHighlightData && (
        <DeleteHighlightModal
          isOpen={isDeleteHighlightModalOpen}
          onClose={() => {
            setIsDeleteHighlightModalOpen(false);
            setDeleteHighlightData(null);
            // Don't clear highlightData here as it might be used by drawer
          }}
          highlightId={deleteHighlightData.highlightId}
          highlightName={deleteHighlightData.highlightName}
          hasStitched={Boolean(highlightData?.output_storage_path || highlightData?.output_url)}
          hasEnhanced={Boolean(highlightData?.enhanced_storage_path || highlightData?.enhanced_url)}
          onSuccess={handleDeleteHighlightSuccess}
          onToast={showToast}
        />
      )}

      {/* Delete Tactics Modal */}
      {deleteTacticsData && (
        <DeleteTacticsModal
          isOpen={isDeleteTacticsModalOpen}
          onClose={() => {
            setIsDeleteTacticsModalOpen(false);
            setDeleteTacticsData(null);
          }}
          tacticsId={deleteTacticsData.tacticsId}
          tacticsName={deleteTacticsData.tacticsName}
          onSuccess={handleDeleteTacticsSuccess}
          onToast={showToast}
        />
      )}

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={5000}
      />
    </div>
  );
};

export default HighlightsTacticsPage;

